import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { AlertCircle, ArrowLeft, Building } from 'lucide-react'
import { apiJson } from '../api'
import { canManageOrgUnits } from '../auth'

interface OrgUnit {
  id: string
  name: string
  code: string
  description?: string
  parentId: string | null
}

const emptyForm = { name: '', code: '', description: '', parentId: '' }

const inputCls =
  'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-brand-400 focus:ring-4 focus:ring-brand-100'
const labelCls = 'mb-1.5 block text-xs font-semibold text-slate-500'

export default function OrgUnitForm() {
  const { id } = useParams()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const location = useLocation()
  const allowManage = canManageOrgUnits()

  const [form, setForm] = useState(emptyForm)
  const [parentOptions, setParentOptions] = useState<OrgUnit[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!allowManage) return
    const fromState = (location.state as { unit?: OrgUnit } | null)?.unit
    apiJson<OrgUnit[]>('/org-units')
      .then((flat) => {
        setParentOptions(flat)
        if (isEdit) {
          const unit = fromState ?? flat.find((u) => u.id === id)
          if (!unit) throw new Error('Org unit not found')
          setForm({
            name: unit.name,
            code: unit.code ?? '',
            description: unit.description ?? '',
            parentId: unit.parentId ?? '',
          })
        }
        setLoading(false)
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load org units')
        setLoading(false)
      })
  }, [allowManage, id, isEdit, location.state])

  const save = async () => {
    setSaving(true)
    setError('')
    const payload: Record<string, unknown> = {
      name: form.name.trim(),
      code: form.code.trim() || undefined,
      description: form.description.trim() || undefined,
    }
    // TypeORM materialized-path trees link parents via the relation object
    if (!isEdit && form.parentId) payload.parent = { id: form.parentId }
    try {
      if (isEdit) {
        await apiJson(`/org-units/${id}`, { method: 'PATCH', body: JSON.stringify(payload), headers: { 'Content-Type': 'application/json' } })
      } else {
        await apiJson('/org-units', { method: 'POST', body: JSON.stringify(payload), headers: { 'Content-Type': 'application/json' } })
      }
      navigate('/org-units')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
      setSaving(false)
    }
  }

  if (!allowManage) {
    return (
      <div className="flex items-center gap-2.5 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-700">
        <AlertCircle size={16} className="shrink-0" />
        You have read-only access to the organization structure.
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-xl space-y-5">
      <Link to="/org-units" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition-colors hover:text-brand-600">
        <ArrowLeft size={15} /> Back to Org Units
      </Link>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-3.5 border-b border-slate-100 px-6 py-5">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-md shadow-brand-500/25">
            <Building size={20} />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-tight">{isEdit ? `Edit ${form.name || 'Org Unit'}` : 'Add Org Unit'}</h1>
            <p className="text-xs text-slate-400">{isEdit ? 'Updates the unit via PATCH /org-units' : 'Creates a unit via POST /org-units'}</p>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            save()
          }}
          className="space-y-4 px-6 py-6"
        >
          <div>
            <label className={labelCls}>Name *</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Engineering" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Code</label>
            <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="ENG" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
          </div>
          {!isEdit && (
            <div>
              <label className={labelCls}>Parent unit</label>
              <select value={form.parentId} onChange={(e) => setForm({ ...form, parentId: e.target.value })} className={inputCls}>
                <option value="">— Top level (root) —</option>
                {parentOptions.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={16} className="shrink-0" />
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-1">
            <Link
              to="/org-units"
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:brightness-110 disabled:opacity-50"
            >
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create unit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
